import React from 'react'
import { PLAN_FEATURED, PLAN_PLAIN, CTA_FILL, CTA_OUTLINE } from 'common/tw/ui'
import { Term, TermOption } from '../types'
import { TERM_LABELS } from '../constants'
import DiscountPill from './DiscountPill'
import FeatureRow from './FeatureRow'

interface PlanCardProps {
  planName: string
  priceDisplay: string // e.g. "79"
  // Struck-through price this one replaces, when discounted.
  originalPriceDisplay?: string
  // e.g. "50% OFF"
  discountLabel?: string
  // Small grey corner chip, e.g. "Legacy".
  chip?: string
  priceUnit: string // e.g. "per teacher · year"
  subtitle?: string // small grey line under the price
  note?: string // emphasised italic note under the subtitle
  features: string[]
  ctaLabel: string
  onCheckout: () => void
  // Highlighted treatment (gold border/glow + gold CTA). Off for the secondary Premium box.
  recommended?: boolean
  // Whether the gold "RECOMMENDED" badge sits on the top border (family omits it).
  showBadge?: boolean
  // Term toggle (teacher only)
  termOptions?: TermOption[]
  term?: Term
  onTermChange?: (term: Term) => void
}

function PlanCard({
  planName,
  priceDisplay,
  originalPriceDisplay,
  discountLabel,
  chip,
  priceUnit,
  subtitle,
  note,
  features,
  ctaLabel,
  onCheckout,
  recommended,
  showBadge,
  termOptions,
  term,
  onTermChange,
}: PlanCardProps) {
  const showToggle = !!(
    termOptions &&
    termOptions.length > 1 &&
    term &&
    onTermChange
  )
  const selectedIndex =
    termOptions && term
      ? Math.max(
          0,
          termOptions.findIndex((o) => o.term === term)
        )
      : 0

  return (
    <div className='relative mt-4'>
      {recommended && showBadge && (
        <span className='absolute -top-2.5 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-pill bg-brand px-3 py-1 text-10 font-bold uppercase tracking-caps text-ink'>
          Recommended
        </span>
      )}
      <div className={recommended ? PLAN_FEATURED : PLAN_PLAIN}>
        {showToggle && (
          <div className='relative mb-5 flex rounded-pill bg-line-200 p-1'>
            {/* Sliding white highlight that animates between segments. */}
            <span
              aria-hidden
              className='absolute bottom-1 left-1 top-1 rounded-pill bg-white shadow-card transition-transform duration-300 ease-out'
              style={{
                width: `calc((100% - 0.5rem) / ${termOptions!.length})`,
                transform: `translateX(${selectedIndex * 100}%)`,
              }}
            />
            {termOptions!.map((opt) => {
              const selected = opt.term === term
              return (
                <button
                  key={opt.term}
                  type='button'
                  aria-pressed={selected}
                  onClick={() => onTermChange!(opt.term)}
                  className={`relative z-10 flex-1 select-none whitespace-nowrap rounded-pill px-4 py-1.5 text-center font-display text-13 transition-colors ${
                    selected
                      ? 'font-bold text-ink'
                      : 'font-semibold text-ink-500'
                  }`}
                >
                  {TERM_LABELS[opt.term]} · ${opt.price}
                </button>
              )
            })}
          </div>
        )}

        <div className='flex items-center justify-between gap-2'>
          <h3 className='font-display text-15 font-bold text-ink'>
            {planName}
          </h3>
          {chip && (
            <span className='whitespace-nowrap rounded-pill border border-line-200 bg-line-100 px-2.5 py-0.5 text-10 font-semibold text-ink-500'>
              {chip}
            </span>
          )}
        </div>

        {/* Price + features crossfade when the term changes (keyed remount fades the new copy in). */}
        <div key={term ?? planName} className='anim-up flex flex-1 flex-col'>
          {/* On a phone the unit drops to its own line, after the pill. */}
          <div className='mt-2.5 flex flex-wrap items-baseline gap-2.5'>
            <span className='font-display text-40 font-extrabold leading-none tracking-display text-ink'>
              ${priceDisplay}
            </span>
            {originalPriceDisplay && (
              <span className='font-display text-20 font-bold text-line-400 line-through'>
                ${originalPriceDisplay}
              </span>
            )}
            {discountLabel && (
              <span className='order-1 sm:order-none'>
                <DiscountPill label={discountLabel} />
              </span>
            )}
            <span className='order-2 basis-full whitespace-nowrap text-17 font-medium text-ink-500 sm:order-none sm:basis-auto'>
              {priceUnit}
            </span>
          </div>

          {subtitle && (
            <p className='mt-1.5 text-xs text-ink-500'>{subtitle}</p>
          )}
          {note && (
            <p className='mt-1.5 text-xs italic text-brand-700'>{note}</p>
          )}

          <ul className='my-5 flex flex-1 flex-col gap-2.5'>
            {features.map((feature) => (
              <FeatureRow key={feature} feature={feature} />
            ))}
          </ul>
        </div>

        <button
          type='button'
          onClick={onCheckout}
          className={recommended ? CTA_FILL : CTA_OUTLINE}
        >
          {ctaLabel} ›
        </button>
      </div>
    </div>
  )
}

PlanCard.defaultProps = {
  originalPriceDisplay: undefined,
  discountLabel: undefined,
  chip: undefined,
  subtitle: undefined,
  note: undefined,
  recommended: true,
  showBadge: true,
  termOptions: undefined,
  term: undefined,
  onTermChange: undefined,
}

export default PlanCard
